export interface TimelineItem {
  date: string;
  title: string;
  text: string;
}

export interface ContentSection {
  id: string;
  heading: string;
  paragraphs: string[];
}

export interface ResponseItem {
  party: string;
  text: string;
}

export interface Entry {
  slug: string;
  title: string;
  category: string;
  categoryName: string;
  summary: string;
  cover: string;
  publishedAt: string;
  updatedAt: string;
  readMinutes: number;
  views: number;
  tags: string[];
  lead: string;
  timeline: TimelineItem[];
  sections: ContentSection[];
  responses: ResponseItem[];
  verdict: string;
}

export interface Category {
  slug: string;
  name: string;
  intro: string;
  icon: string;
}

export const categories: Category[] = [
  {
    slug: "star",
    name: "娱乐明星",
    intro: "围绕影视、音乐从业者的公开作品争议与工作动态展开的事件梳理。",
    icon: "ri-movie-2-line",
  },
  {
    slug: "idol",
    name: "饭圈文化",
    intro: "应援、控评、周边等粉丝社群行为的现象记录与反思。",
    icon: "ri-heart-3-line",
  },
  {
    slug: "live",
    name: "直播电竞",
    intro: "直播平台、电竞赛事与选手俱乐部之间的公开纠纷与赛制讨论。",
    icon: "ri-gamepad-line",
  },
  {
    slug: "creator",
    name: "网红达人",
    intro: "内容创作者与品牌合作、内容合规相关的公开事件整理。",
    icon: "ri-video-line",
  },
  {
    slug: "biz",
    name: "商业消费",
    intro: "餐饮、零售与消费品领域因排队、定价、服务引发的公开讨论。",
    icon: "ri-store-2-line",
  },
  {
    slug: "show",
    name: "演出综艺",
    intro: "音乐节、演唱会与综艺节目在票务、剪辑、赛制上的公开争议。",
    icon: "ri-mic-line",
  },
];

export const entries: Entry[] = [
  {
    slug: "yeying-live-substitute-row",
    title: "夜莺直播周赛代打风波",
    category: "live",
    categoryName: "直播电竞",
    summary:
      "一场平台周赛的冠军归属引发选手代打质疑，赛事方三次调取对局日志后给出结论。",
    cover:
      "https://readdy.ai/api/search-image?query=Neon%20lit%20esports%20arena%20stage%20with%20empty%20gaming%20chairs%20and%20large%20screens%2C%20moody%20professional%20photography%2C%20warm%20orange%20stage%20lighting%20on%20dark%20background%2C%20high%20detail&width=800&height=500&seq=hl-entry-live-01&orientation=landscape",
    publishedAt: "2026-05-18",
    updatedAt: "2026-05-27",
    readMinutes: 6,
    views: 48210,
    tags: ["直播赛事", "选手合约", "赛制争议"],
    lead:
      "一场常规的线上周赛，因为决赛第三局的操作节奏异常，被观众截取出来反复比对，最终演变成持续十天的代打质疑。",
    timeline: [
      { date: "5 月 14 日", title: "周赛决赛结束", text: "选手「夜莺」以 3:1 的比分拿下平台周赛冠军，直播间同步冲上热榜。" },
      { date: "5 月 16 日", title: "操作对比帖出现", text: "有观众将对局录像与选手过往直播操作风格并列比对，指出走位习惯存在明显差异。" },
      { date: "5 月 18 日", title: "赛事方启动复核", text: "平台公告称已调取该场比赛的对局日志与设备登录记录，复核期间奖项暂缓发放。" },
      { date: "5 月 24 日", title: "公布复核结论", text: "平台确认该场比赛设备登录地址存在异地情况，判定比赛结果无效。" },
      { date: "5 月 27 日", title: "选手发布回应", text: "当事人通过个人账号回应，承认因身体原因请他人代为上场，并退出当月全部赛事。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "该平台周赛是面向签约主播的常规赛事，冠军可直接获得下月线下赛的直通名额，因此对选手的曝光度影响较大。夜莺在赛事前一周刚结束密集的日常直播排期，赛前状态曾被粉丝关注。",
          "周赛采用线上赛制，选手在各自直播间完成对局，平台通过设备指纹与登录日志进行身份核验。这一机制在此前的赛事中运行平稳，但也正因为它依赖线上环境，才让本次质疑得以被快速定位。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "决赛结束后两天，一段将两场比赛操作逐帧并列的对比视频在社区传播。视频作者认为，选手在关键团战中的鼠标移动轨迹与往日直播存在可量化的差异，并附上了操作频率统计。",
          "对比视频并未直接给出结论，但引发了大量二次解读。部分观众转而批评赛事核验流程不严谨，另一部分观众则认为仅凭操作风格判断代打缺乏依据。争议在第三天达到峰值，赛事方随即宣布启动复核。",
          "复核持续了六天，期间赛事方两次补充说明进展，坦言需要交叉比对多台设备的登录记录。最终结论公布后，平台同步更新了后续赛事的核验规则。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "平台在结论公告中列明了三条依据：比赛时段的设备登录地址与选手备案信息不一致、对局账号在比赛前后存在异地登录、以及现场直播画面与账号操作时间轴存在错位。",
          "基于以上依据，赛事方判定该场决赛结果无效，冠军顺延至亚军选手，同时取消夜莺在当月剩余赛事的参赛资格。平台表示不会就个人行为追加处罚，但会将核验流程升级为赛前必检。",
        ],
      },
    ],
    responses: [
      { party: "赛事平台", text: "核验流程存在可优化空间，后续将增加赛前设备确认与赛中随机抽查两个环节。" },
      { party: "选手本人", text: "承认请他人代打的事实，向赛事方与观众致歉，并宣布退出当月全部赛事。" },
      { party: "亚军俱乐部", text: "接受顺延结果，呼吁各方把讨论焦点放回赛事规则本身。" },
    ],
    verdict:
      "这是典型的「流程型争议」：核心矛盾并非谁对谁错，而是线上赛制在身份核验上的漏洞。对观众而言，值得关注的不是情绪化的阵营站队，而是赛事方是否真的把漏洞补上。",
  },
  {
    slug: "star-girl-support-color-dispute",
    title: "星野少女应援色争议始末",
    category: "idol",
    categoryName: "饭圈文化",
    summary:
      "应援色被指与前辈团体重合，粉丝社群内部出现两种截然不同的处理意见。",
    cover:
      "https://readdy.ai/api/search-image?query=Colorful%20concert%20light%20sticks%20glowing%20in%20a%20dark%20audience%2C%20bokeh%20lights%2C%20warm%20amber%20and%20red%20tones%2C%20artistic%20concert%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-idol-02&orientation=landscape",
    publishedAt: "2026-06-02",
    updatedAt: "2026-06-09",
    readMinutes: 5,
    views: 33670,
    tags: ["应援文化", "粉丝社群", "颜色规范"],
    lead:
      "应援色在偶像文化里承担着识别功能，一旦与既有团体重合，讨论往往就会从「好不好看」转向「合不合规」。",
    timeline: [
      { date: "5 月 30 日", title: "官方公布应援色", text: "团体官方账号发布应援色为「暖橘金」，并同步上架了配套荧光棒。" },
      { date: "5 月 31 日", title: "相似度讨论出现", text: "有粉丝指出该颜色与另一支前辈团体长期使用的应援色接近，并贴出色值对比。" },
      { date: "6 月 3 日", title: "社群意见分流", text: "粉丝站内部形成「主动更换」与「保留颜色并表示尊重」两种意见，双方各自发布长文。" },
      { date: "6 月 7 日", title: "官方说明", text: "运营方回应应援色由设计团队独立选定，此前未做同色系比对，将评估是否调整。" },
      { date: "6 月 9 日", title: "确定为渐变方案", text: "官方宣布保留暖橘为主色，增加由金至粉的渐变以形成区分，周边下月起更新。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "应援色在偶像文化中是一种低成本的身份标识：观众举起同一颜色的灯牌，就能在场馆中快速辨认出同团粉丝。由于颜色数量有限，不同团体之间的撞色其实相当常见。",
          "真正让这次讨论升温的，是应援色背后约定俗成的「避让」习惯。这一习惯并没有成文规则，更多依赖社群内部的口碑与默契，因此一旦被打破，很容易演变为关于「态度」的争论。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "公布应援色当晚，色值对比表在多个粉丝社区流传。表格显示两支团体的应援色在常规灯光下相近，但在高饱和度屏幕下差异明显。这个细节让讨论立刻分成了两派。",
          "主张更换的一派认为，避让是社群长期形成的礼貌，主动调整成本很低；主张保留的一派则认为，颜色并不属于任何团体，过度强调避让反而会限制新人团体的表达空间。",
          "值得注意的是，两派在争论过程中都刻意避免了对前辈团体及其粉丝的攻击，讨论整体停留在规则层面，这也是本次事件没有进一步升级的原因之一。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "运营方在事件第八天给出说明，坦承应援色确定时只做了内部评估，未进行跨团体的同色系排查。随后公布的方案保留了暖橘主色，但加入渐变过渡以增强辨认度，周边产品同步更新。",
          "粉丝站随后撤下了此前发布的内部争论长文，转而统一发布渐变灯牌的使用说明。整件事以一次流程优化收尾。",
        ],
      },
    ],
    responses: [
      { party: "运营方", text: "确认应援色选定流程缺少同色系比对环节，已补充进后续视觉规范。" },
      { party: "粉丝站", text: "接受渐变方案，呼吁不再就该话题展开阵营化讨论。" },
    ],
    verdict:
      "应援色本身没有排他性，真正需要的是可预期的沟通流程。当「默契」变成默认规则，任何新团体都可能踩线；把比对环节写进流程，比事后争论更有意义。",
  },
  {
    slug: "short-drama-script-similarity",
    title: "短剧剧本相似度争议复盘",
    category: "show",
    categoryName: "演出综艺",
    summary:
      "两部同期上线的短剧被指情节结构高度接近，制作方各自公布了创作时间线。",
    cover:
      "https://readdy.ai/api/search-image?query=Stack%20of%20printed%20film%20scripts%20and%20storyboards%20on%20a%20wooden%20desk%2C%20warm%20window%20light%2C%20editorial%20still%20life%20photography%2C%20muted%20amber%20tones%2C%20high%20detail&width=800&height=500&seq=hl-entry-show-03&orientation=landscape",
    publishedAt: "2026-04-21",
    updatedAt: "2026-05-04",
    readMinutes: 7,
    views: 27430,
    tags: ["短剧", "剧本创作", "版权讨论"],
    lead:
      "短剧的更新节奏极快，同一个热门题材在几周内被反复改编已是常态，这也让「相似」与「借鉴」的边界变得格外模糊。",
    timeline: [
      { date: "4 月 12 日", title: "两部短剧同期上线", text: "两部都市题材短剧在同一周开播，均采用「失忆重逢」的主线设定。" },
      { date: "4 月 18 日", title: "对比长文出现", text: "有观众整理出两剧前八集的分集结构对照表，指出多个节点高度接近。" },
      { date: "4 月 22 日", title: "A 方公布创作时间线", text: "A 剧编剧公开大纲留档记录，显示核心设定形成于去年冬天。" },
      { date: "4 月 26 日", title: "B 方作出说明", text: "B 剧制作方回应称剧本为独立创作，题材重叠源于市场同质化。" },
      { date: "5 月 4 日", title: "双方各自收尾", text: "两剧均按原计划播完，未进入法律程序，但相关讨论推动了平台的原创标注机制。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "短剧的商业模式决定了它对热点题材的响应速度。一个设定被验证有效后，往往会在短期内出现大量同类作品，这种「题材聚集」本身并不等于抄袭。",
          "但结构层面的相似性更难解释。本次争议的焦点正是分集结构：两部剧在前八集中，关键转折出现的位置几乎一致，这让不少人认为超出了巧合的范围。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "对照表发布后，讨论很快从「像不像」转向「怎么证明」。A 剧编剧公布了大纲留档与创作群聊记录，时间点早于 B 剧立项；B 剧方面则强调剧本的独立创作过程，并提供了一份创作说明。",
          "由于短剧的创作周期普遍较短，双方提供的时间线在部分节点上存在重叠区间，这也让外界难以形成一致判断。平台在此期间并未公开介入，仅提示创作者做好创作留痕。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "事件最终没有进入法律程序。两部短剧按原计划播完，热度也随着更新结束逐步回落。但它留下的影响相当具体：多家短剧平台在立项环节增加了「创作留痕」提示，鼓励团队保留大纲与修订记录。",
          "从行业角度看，这场讨论的价值在于把一个长期被忽略的问题摆上台面——当创作周期被极度压缩，如何证明「我先想到的」。",
        ],
      },
    ],
    responses: [
      { party: "A 剧编剧", text: "公开大纲与修订留档，希望把讨论引向创作保护机制。" },
      { party: "B 剧制作方", text: "强调独立创作，认为题材重叠主要来自市场同质化。" },
      { party: "播出平台", text: "提示创作者做好创作留痕，暂未就个案作出裁定。" },
    ],
    verdict:
      "这类争议很难有非黑即白的答案。对观众来说，与其急着定论，不如关注平台是否真的建立起可执行的创作留痕与申诉机制。",
  },
  {
    slug: "food-blogger-group-buy-backlash",
    title: "美食博主带货翻车始末",
    category: "creator",
    categoryName: "网红达人",
    summary:
      "一款被形容为「入口即化」的预制菜在收货后被大量反馈口感不符，博主公布了选品全流程。",
    cover:
      "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20packaged%20ready%20meals%20and%20a%20camera%20on%20a%20kitchen%20table%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-creator-04&orientation=landscape",
    publishedAt: "2026-03-15",
    updatedAt: "2026-03-29",
    readMinutes: 6,
    views: 51980,
    tags: ["直播带货", "选品流程", "售后处理"],
    lead:
      "从「推荐」到「翻车」，中间隔着的往往不是良心，而是一整套没有被认真执行的选品流程。",
    timeline: [
      { date: "3 月 8 日", title: "专场直播开卖", text: "博主在美食专场中推荐一款预制菜，直播期间成交额突破预期。" },
      { date: "3 月 11 日", title: "收货反馈集中出现", text: "多位买家反馈加热后口感与直播描述存在差距，包装说明也与口播内容不一致。" },
      { date: "3 月 13 日", title: "博主暂停直播", text: "博主宣布暂停后续专场，先行处理售后，并公开向买家征集问题样本。" },
      { date: "3 月 20 日", title: "公布选品复盘", text: "团队公开选品流程记录，承认样品与实际批次之间未做二次比对。" },
      { date: "3 月 29 日", title: "公布处理方案", text: "对所有订单开放无理由全额退款，并终止与该供应商的合作。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "预制菜在直播间的售卖依赖口播描述建立预期，而「入口即化」「现炒口感」这类形容本身带有明显的主观色彩。当描述与实物落差过大，退货与投诉就会集中爆发。",
          "这位博主此前的口碑建立在「亲自试吃」的人设上，因此本次翻车对其信任度的冲击比一般带货事件更大。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "反馈最早出现在收货后的第三天，集中指向两点：一是加热后的口感与直播描述差距明显，二是包装上的配料标注与口播内容不符。",
          "博主在反馈出现的第二天就暂停了后续专场，这个反应速度比多数同类事件要快。随后团队公开了选品记录，显示问题出在最关键的一环——试吃样品与实际发货批次不是同一批。",
          "在处理方案公布前，博主连续开了两场说明直播，逐条回应买家提问，没有删除评论或关闭弹幕。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "处理方案包括三项：对涉及订单开放无理由全额退款、终止与供应商的合作、在选品流程中增加「到货批次二次试吃」环节。",
          "事件平息后，博主的粉丝量短期内出现下滑，但后续直播的复购率反而有所回升。这被不少同行视为「处理得当反而修复信任」的案例。",
        ],
      },
    ],
    responses: [
      { party: "博主团队", text: "承认样品与发货批次未做二次比对，公开选品流程并开放全额退款。" },
      { party: "供应商", text: "表示不同批次存在口味差异，愿意配合处理售后。" },
    ],
    verdict:
      "带货翻车的核心往往不是「推荐了坏东西」，而是「没有验证」。把试吃范围从样品扩展到实际发货批次，是一个成本不高但很有效的补丁。",
  },
  {
    slug: "streamer-contract-dispute",
    title: "游戏主播合约纠纷事件整理",
    category: "live",
    categoryName: "直播电竞",
    summary:
      "主播与公会在分成比例与独家条款上产生分歧，双方先后公开了部分合约内容。",
    cover:
      "https://readdy.ai/api/search-image?query=Modern%20streaming%20setup%20with%20microphone%20and%20dual%20monitors%20in%20a%20dim%20room%2C%20warm%20desk%20lamp%20glow%2C%20moody%20editorial%20interior%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-live-05&orientation=landscape",
    publishedAt: "2026-02-24",
    updatedAt: "2026-03-08",
    readMinutes: 7,
    views: 39870,
    tags: ["主播合约", "公会分成", "独家条款"],
    lead:
      "一份为期三年的独家合约，因为一场联动活动的分成计算方式，被推到了公开讨论的台面上。",
    timeline: [
      { date: "2 月 18 日", title: "联动活动结束", text: "主播参与平台联动活动，活动收益按公会约定比例结算。" },
      { date: "2 月 20 日", title: "结算结果分歧", text: "主播对到账金额提出异议，认为活动补贴未计入分成基数。" },
      { date: "2 月 24 日", title: "主播公开发声", text: "主播在个人账号说明情况，公布合约中关于分成基数的部分条款。" },
      { date: "2 月 27 日", title: "公会回应", text: "公会发布声明，称分成计算符合合约约定，并指出主播存在私下接单情况。" },
      { date: "3 月 8 日", title: "达成解约", text: "双方宣布协商解约，主播合约剩余期限由新平台承接，具体条件未公开。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "公会与主播的合约通常包含分成比例、独家播出、活动参与等条款。其中「分成基数」是最容易产生分歧的部分——补贴是否计入、礼物与广告收益如何区分，往往决定最终到账金额。",
          "这份合约为期三年，属于行业内的常见期限。主播在合约第二年已进入稳定期，收入结构与签约时相比发生了变化，这也是分歧产生的土壤。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "分歧最初只在结算沟通中体现。主播认为活动补贴属于劳动所得，应计入分成基数；公会则依据合约中「平台补贴不计入分成」的表述，按原方式结算。",
          "主播公开发声后，双方先后晒出了合约的部分条款截图。由于截图均不完整，外界对「分成基数」的具体定义各执一词，讨论一度失焦。",
          "随后公会提及主播存在私下接单情况，主播则回应称相关合作均已报备。这一来一回让事件从单纯的结算争议，扩展到合约履行层面。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "在平台介入协调后，双方于 3 月上旬宣布协商解约。主播的剩余合约由新平台承接，独家条款相应地转移。具体的经济补偿条件未对外公开。",
          "公会随后更新了模板合约，把「平台活动补贴是否计入分成基数」单独列为一条，并增加了私下合作的报备流程说明。",
        ],
      },
    ],
    responses: [
      { party: "主播", text: "认为活动补贴应计入分成基数，公开部分合约条款以说明立场。" },
      { party: "所属公会", text: "称结算方式符合合约约定，并指出存在未经报备的私下合作。" },
      { party: "平台方", text: "作为协调方介入，协助双方完成解约与新合约承接。" },
    ],
    verdict:
      "合约纠纷的公开化，往往是因为条款本身留有解释空间。与其争论谁更有理，不如把「分成基数」这类模糊表述在签约时就写清楚。",
  },
  {
    slug: "variety-show-editing-dispute",
    title: "综艺剪辑争议完整记录",
    category: "show",
    categoryName: "演出综艺",
    summary:
      "某期竞演综艺的成片被指出放大了嘉宾的失误片段，节目组公布了原始录制素材说明。",
    cover:
      "https://readdy.ai/api/search-image?query=Television%20studio%20control%20room%20with%20editing%20screens%20and%20mixing%20console%2C%20warm%20amber%20indicator%20lights%2C%20moody%20broadcast%20production%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-show-06&orientation=landscape",
    publishedAt: "2026-01-28",
    updatedAt: "2026-02-11",
    readMinutes: 6,
    views: 30120,
    tags: ["综艺剪辑", "竞演赛制", "节目组回应"],
    lead:
      "同一段录制素材，剪辑顺序不同，观众看到的人物形象就可能完全不同，这正是剪辑争议反复出现的根本原因。",
    timeline: [
      { date: "1 月 22 日", title: "当期节目播出", text: "竞演综艺第 6 期播出，一位嘉宾的舞台失误被完整呈现并反复回放。" },
      { date: "1 月 24 日", title: "观众提出质疑", text: "有观众比对现场录制返图，认为失误片段在成片中占比明显偏高。" },
      { date: "1 月 26 日", title: "节目组回应素材", text: "节目组放出未剪辑的原始录制片段，说明成片时长分配依据赛制需要。" },
      { date: "2 月 3 日", title: "赛制说明补充", text: "节目组补充说明竞演段的镜头分配规则，承认部分转场衔接可再优化。" },
      { date: "2 月 11 日", title: "争议逐步平息", text: "后续节目调整了失误片段的回放次数，相关讨论随之减少。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "竞演类综艺的成片需要在有限时长内完成叙事，必然涉及素材的取舍。哪些镜头保留、哪些镜头放大，既影响观感，也直接影响观众对嘉宾的评价。",
          "本期节目的争议点在于「重复回放」：同一处失误在成片中被不同角度呈现多次，客观上强化了观众对这一片段的印象。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "质疑最初来自现场观众的返图对比。由于返图显示嘉宾在其他环节表现正常，观众开始怀疑成片的镜头分配失衡。",
          "节目组随后公开了该环节的原始录制片段，说明成片时长是根据竞演评分权重分配的，并强调没有主观恶意。但同时，节目组也承认在转场衔接上存在优化空间。",
          "讨论持续了约两周。由于节目组回应相对及时，且没有删除相关讨论，争议没有升级为更大范围的抵制。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "后续几期节目中，失误片段的回放次数明显减少，转场节奏也有所调整。节目组未再就此事单独发布公告，争议随节目推进自然回落。",
          "从结果看，这次事件的价值在于让「镜头分配规则」被公开讨论了一次——观众得以了解成片背后的取舍逻辑，而不是只看到最终呈现。",
        ],
      },
    ],
    responses: [
      { party: "节目组", text: "公开原始录制片段，说明镜头按时长权重分配，承认转场可优化。" },
      { party: "嘉宾方", text: "未单独发声，工作室表示尊重节目组的创作安排。" },
    ],
    verdict:
      "剪辑没有绝对的中立，但可以不透明与透明之分。把分配规则讲清楚，比反复强调「没有恶意」更能消解质疑。",
  },
  {
    slug: "virtual-idol-voice-rumor",
    title: "虚拟偶像中之人传言梳理",
    category: "idol",
    categoryName: "饭圈文化",
    summary:
      "一则关于虚拟偶像幕后配音的传言在社群扩散，运营方发布了声明并进行了直播验证。",
    cover:
      "https://readdy.ai/api/search-image?query=Holographic%20virtual%20performer%20silhouette%20on%20stage%20with%20abstract%20light%20ribbons%2C%20warm%20amber%20and%20crimson%20glow%2C%20futuristic%20digital%20art%20style%2C%20high%20detail&width=800&height=500&seq=hl-entry-idol-07&orientation=landscape",
    publishedAt: "2026-06-20",
    updatedAt: "2026-06-28",
    readMinutes: 5,
    views: 25640,
    tags: ["虚拟偶像", "社群传言", "官方声明"],
    lead:
      "虚拟形象与幕后表演者之间的距离，本身就是这类企划刻意保留的空间，一旦被传言打破，讨论往往会迅速失控。",
    timeline: [
      { date: "6 月 17 日", title: "传言开始扩散", text: "社群中出现关于该虚拟偶像幕后表演者身份的对比帖，附带音频片段。" },
      { date: "6 月 19 日", title: "衍生讨论升级", text: "部分账号据此对企划方进行指责，相关话题进入多平台热榜。" },
      { date: "6 月 21 日", title: "运营方发布声明", text: "运营方说明幕后人员配置属于正常合约安排，传言中的比对依据不成立。" },
      { date: "6 月 25 日", title: "安排验证直播", text: "企划方安排一场无剪辑的实时演出直播，回应核心质疑。" },
      { date: "6 月 28 日", title: "话题回落", text: "社群管理团队清理了针对个人的攻击性内容，讨论逐步平息。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "虚拟偶像企划通常会刻意模糊幕后表演者的公开信息，这既是为了维护角色设定，也是对表演者本人的一种保护。这种距离感本身是企划设计的一部分。",
          "当传言把幕后信息与角色绑定，粉丝社群容易出现分裂：一部分人希望知道真相，另一部分人则担心过度曝光会破坏角色形象。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "传言最初以「音频比对」的形式出现，作者声称通过频谱分析可以判断幕后的实际人员。这类分析缺乏公开可验证的方法，却因为视觉化的图表而获得了传播力。",
          "运营方在传言扩散两天后发布声明，指出比对方法不成立，同时强调幕后人员配置属于正常合约安排，不存在外界猜测的情况。为回应核心质疑，企划方额外安排了一场无剪辑的实时演出直播。",
          "直播结束后，社群管理团队开始清理针对具体个人的攻击性内容，话题热度随之下降。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "事件以运营方的声明与验证直播收尾，没有进入法律程序。企划方在后续更新中调整了内容排期，把幕后制作的公开程度作为可选项交给社群讨论。",
          "这件事之后，多个虚拟偶像社群自发制定了关于「幕后信息」的讨论规范，明确禁止针对个人的身份挖掘。",
        ],
      },
    ],
    responses: [
      { party: "企划运营方", text: "声明传闻依据不成立，安排无剪辑直播回应核心质疑。" },
      { party: "社群管理团队", text: "清理攻击性内容，推动制定幕后信息讨论规范。" },
    ],
    verdict:
      "传言最大的杀伤力不在于真假，而在于它把抽象质疑变成了对具体个人的攻击。区分「讨论企划」与「挖掘个人」，是社群能否长期健康的关键。",
  },
  {
    slug: "bakery-queue-incident",
    title: "网红蛋糕店排队风波",
    category: "biz",
    categoryName: "商业消费",
    summary:
      "门店因限购与取号规则调整引发排队顾客不满，店家公布了排队机制说明。",
    cover:
      "https://readdy.ai/api/search-image?query=Warm%20minimal%20bakery%20counter%20with%20fresh%20cakes%20and%20a%20queue%20barrier%2C%20soft%20daylight%2C%20amber%20and%20cream%20tones%2C%20editorial%20food%20retail%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-biz-08&orientation=landscape",
    publishedAt: "2026-05-05",
    updatedAt: "2026-05-16",
    readMinutes: 5,
    views: 22890,
    tags: ["门店运营", "限购规则", "消费体验"],
    lead:
      "排队本身是流量证明，但当等待时间与购买体验严重失衡时，网红店的热度也可能迅速反噬。",
    timeline: [
      { date: "5 月 1 日", title: "假期客流激增", text: "假期首日门店客流达到日常的数倍，队伍一度超出商场规定区域。" },
      { date: "5 月 2 日", title: "临时限购调整", text: "门店临时将每人限购数量从 6 份下调为 2 份，现场未提前公告。" },
      { date: "5 月 4 日", title: "顾客反馈集中出现", text: "多位顾客反映排队数小时仍未购得，对临时调整表达不满。" },
      { date: "5 月 8 日", title: "门店发布说明", text: "门店公布排队与限购机制说明，致歉并承诺提前公告规则调整。" },
      { date: "5 月 16 日", title: "改为线上取号", text: "门店上线线上取号与分时段购买，现场排队情况明显缓解。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "这家门店因某款蛋糕在社交平台走红，节假日客流长期超出门店接待能力。此前的限购规则为每人 6 份，假期首日依然出现了长时间排队。",
          "问题不在于限购本身，而在于调整来得突然——顾客在排队数小时后才得知规则变化，这种落差放大了不满情绪。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "5 月 2 日的临时限购调整是在客流超出预期后作出的现场决定，门店通过店员口头告知，未通过任何线上渠道提前发布。",
          "随后两天，相关的排队照片与等待时长反馈在社交平台集中出现。门店在此期间增加了现场引导人员，但没有及时发布书面说明，导致信息真空被各种猜测填补。",
          "直到 5 月 8 日，门店才正式发布说明，承认规则调整存在沟通不足，并承诺后续变更至少提前一天公告。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "5 月中旬，门店上线了线上取号与分时段购买机制，顾客可以按时间段到店取货，现场排队时长下降明显。",
          "限购规则也改为常设公示，贴在门店入口与线上页面，不再临时口头调整。事件基本平息。",
        ],
      },
    ],
    responses: [
      { party: "门店方", text: "致歉并承认规则调整沟通不足，上线线上取号与分时段购买。" },
      { party: "商场管理方", text: "协助疏导客流，要求商户提前报备大客流预案。" },
    ],
    verdict:
      "排队管理的核心是「预期管理」。规则可以严格，但不能突然；提前公告的成本极低，却能避免绝大多数现场冲突。",
  },
  {
    slug: "music-festival-refund-dispute",
    title: "音乐节退票事件完整梳理",
    category: "show",
    categoryName: "演出综艺",
    summary:
      "因天气原因演出提前结束，主办方先后公布了两版退票方案，最终按比例处理。",
    cover:
      "https://readdy.ai/api/search-image?query=Outdoor%20music%20festival%20stage%20at%20dusk%20with%20empty%20crowd%20area%20and%20warm%20string%20lights%2C%20dramatic%20amber%20sky%2C%20editorial%20event%20photography%2C%20high%20detail&width=800&height=500&seq=hl-entry-show-09&orientation=landscape",
    publishedAt: "2026-04-08",
    updatedAt: "2026-04-25",
    readMinutes: 6,
    views: 36420,
    tags: ["音乐节", "票务退改", "天气预案"],
    lead:
      "天气是不可抗力，但不可抗力并不等于可以省略沟通。退票方案怎么定，直接决定观众是否愿意再来一次。",
    timeline: [
      { date: "4 月 4 日", title: "演出提前结束", text: "音乐节第二日因强对流天气，压轴演出在开始后不久被叫停。" },
      { date: "4 月 5 日", title: "首版方案公布", text: "主办方公布首版方案，仅对未入场的观众提供全额退票。" },
      { date: "4 月 6 日", title: "观众集体反馈", text: "已入场但未看完整场次的观众认为方案未覆盖实际损失。" },
      { date: "4 月 12 日", title: "第二版方案", text: "主办方调整方案，对已入场观众按实际演出时长比例退款。" },
      { date: "4 月 25 日", title: "退款完成", text: "主办方公布退款完成情况，并说明后续场次将增加天气预案说明。" },
    ],
    sections: [
      {
        id: "bg",
        heading: "事件背景",
        paragraphs: [
          "音乐节通常在户外举办，天气风险客观存在。多数票务协议会注明「因不可抗力导致的演出变更」，但具体如何补偿，往往取决于主办方的方案设计。",
          "本次争议的关键在于：当天已入场的观众占了绝大多数，而首版方案只覆盖未入场者，这被普遍认为与实际体验脱节。",
        ],
      },
      {
        id: "process",
        heading: "事件经过",
        paragraphs: [
          "首版方案公布后，社交平台上的反馈迅速集中。观众的诉求并不复杂：按实际观看时长给予相应补偿，而不是简单地以「是否入场」划线。",
          "主办方在收到反馈后没有立即回应，沉默了约一周。这段时间里，讨论从退票方案扩展到对主办方应急能力的质疑。4 月 12 日公布的第二版方案调整为按演出时长比例退款，覆盖了已入场观众。",
          "由于处理周期较长，部分观众的信任已经受到影响，后续场次的售票速度明显放缓。",
        ],
      },
      {
        id: "result",
        heading: "完整记录与结果",
        paragraphs: [
          "退款于 4 月下旬基本完成。主办方在公告中说明，后续场次将在票务页面明确标注天气预案，包括叫停标准与相应补偿比例。",
          "这次事件也促使部分票务平台在商品页增加了「不可抗力补偿说明」的展示位。",
        ],
      },
    ],
    responses: [
      { party: "主办方", text: "调整退票方案为按演出时长比例退款，并补充天气预案说明。" },
      { party: "票务平台", text: "在商品页增加不可抗力补偿说明的展示位。" },
    ],
    verdict:
      "不可抗力可以免责，但不能免沟通。把补偿规则提前写清楚，用户即使遇到天气变化，也不至于觉得被临时通知。",
  },
];

export const faqs = [
  {
    q: "黑料百科的词条是怎么整理的？",
    a: "每个词条都遵循同一套结构：先交代事件背景，再按时间顺序梳理经过，随后完整记录各方公开回应，最后给出一段偏向理性的观察。我们不做主观定性，只把公开可查的信息按时间线排列清楚。",
  },
  {
    q: "词条内容多久更新一次？",
    a: "事件仍在发展时，词条通常会在每个关键节点更新一次，并在页面顶部标注最近更新时间。事件进入平静期后，词条会转为归档状态，仅在有实质性新进展时修订。",
  },
  {
    q: "为什么有些词条没有给出结论？",
    a: "因为很多事件本身就没有明确结论。与其替用户下判断，我们更倾向于把已知事实、各方说法和仍存疑的部分分别列出，让读者自己看清信息边界。",
  },
  {
    q: "发现词条信息有误怎么办？",
    a: "可以通过联系我们页面提交更正说明，并附上可查证的公开来源。核实后我们会更新词条，并在修订记录中标注修改时间与内容。",
  },
  {
    q: "站内内容会涉及真实人物吗？",
    a: "站内词条均以示例性内容呈现，用于说明词条的结构与呈现方式，不针对任何真实人物或机构。我们希望这套结构本身足够清晰，能被用于客观整理公开事件。",
  },
];

export const testimonials = [
  {
    name: "林一舟",
    role: "内容运营",
    text: "时间线功能太实用了。以前跟进一个热点要在十几个账号之间来回翻，现在打开词条就能看到完整脉络，省了大量时间。",
  },
  {
    name: "周予安",
    role: "在校学生",
    text: "最喜欢的是「理性看待」那一段。很多平台只会放大情绪，这里反而会把还存疑的地方单独标出来，看完不会那么上头。",
  },
  {
    name: "陈慕白",
    role: "媒体编辑",
    text: "词条结构很规范，背景、经过、回应分开写，引用来源也标得清楚，我们做二次核对时会直接以这里的记录为起点。",
  },
];